import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { GoalFormatType } from 'webapp/enums/enums';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipComponent from 'webapp/components/tool-tip';

import type Goal from 'webapp/models-custom/goal';

interface SubgoalCompatibilityComponentInterface {
	Args: {
		model: Goal;
	};
}
export default class SubgoalCompatibilityComponent extends Component<SubgoalCompatibilityComponentInterface> {
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;

	get model() {
		return this.args.model;
	}

	get parentGoal() {
		return this.model.parentGoal;
	}

	get subGoalData() {
		return this.model.subGoalData;
	}

	get showWarning() {
		return this.isFormatTypeInvalid || this.isTimeframeInvalid; /// if aligned goal hide warning
	}

	get showTooltip() {
		return !!this.parentGoal && (this.showWarning || this.isTimeframeContinuous);
	}

	get isTimeframeContinuous() {
		return this.model.isTimeframeContinuous;
	}

	get isAlignedChild() {
		return !this.model.parentGoal?.isSubgoalsProgressGoal;
	}

	get isFormatTypeInvalid() {
		if (this.parentGoalFormatType === GoalFormatType.PERCENTAGE || this.isAlignedChild) {
			return false;
		}

		return (
			this.childFormatType &&
			this.parentGoalFormatType &&
			this.childFormatType !== this.parentGoalFormatType
		);
	}

	get isTimeframeInvalid() {
		return !this.subGoalData?.isTimeframeValid;
	}

	get childFormatType() {
		return this.subGoalData?.goalFormat;
	}

	get parentGoalFormatType() {
		return this.parentGoal?.formatType;
	}

	get tooltipText() {
		const leadingText =
			'This sub-goal no longer contributes to the parent goal progress because of a ';
		if (this.isTimeframeInvalid) {
			return `${leadingText}timeframe mismatch.`;
		}
		if (this.isFormatTypeInvalid) {
			return `${leadingText}measurement format mismatch.`;
		}
		if (this.isTimeframeContinuous) {
			return 'This sub-goal was originally set up with a continuous timeframe and is recalculated to match the parent’s timeframe, so its values may differ from the standalone goal.';
		}
		return '';
	}

	get iconName() {
		return this.showWarning ? 'AlertFill' : 'InfoFill';
	}

	<template>
		{{#if this.showTooltip}}
			<div class="compatibility">
				<CommonUiIconComponent @name={{this.iconName}} @size="sm" />
				<ToolTipComponent @text={{this.tooltipText}} @position="top" @maxWidth={{350}} />
			</div>
		{{/if}}
	</template>
}
